import React from 'react';
import { Link2, Award, Zap, CheckCircle, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Helmet } from 'react-helmet';

const HowToEarn = () => {
  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Earn $FLUMBERICO Tokens",
    "description": "Learn how to earn $FLUMBERICO tokens by building high-authority backlinks and competing in the SEO ranking competition for the keyword 'flumberico'.",
    "image": "https://www.flumberico.fun/android-chrome-512x512.png",
    "totalTime": "PT30M",
    "estimatedCost": {
      "@type": "MonetaryAmount",
      "currency": "USD",
      "value": "0"
    },
    "step": [
      {
        "@type": "HowToStep",
        "position": 1,
        "name": "Join Official Telegram",
        "text": "Join the official FLUMBERICO Telegram channel at https://t.me/flumberico to submit your backlinks and claim rewards.",
        "url": "https://t.me/flumberico"
      },
      {
        "@type": "HowToStep",
        "position": 2,
        "name": "Build High-Authority Backlinks",
        "text": "Create or acquire high-quality backlinks from authoritative domains (DA 50+) that link to flumberico.fun. Focus on contextual, editorial links from relevant content.",
        "itemListElement": [
          {
            "@type": "HowToDirection",
            "text": "Target websites with Domain Authority 50 or higher"
          },
          {
            "@type": "HowToDirection",
            "text": "Create valuable content that naturally links to flumberico.fun"
          },
          {
            "@type": "HowToDirection",
            "text": "Use white-hat SEO tactics like guest posting, HARO, and resource page outreach"
          }
        ]
      },
      {
        "@type": "HowToStep",
        "position": 3,
        "name": "Submit Backlinks for Verification",
        "text": "Submit your backlink URLs through the official Telegram channel. Include the referring domain's URL and your Solana wallet address.",
        "url": "https://t.me/flumberico"
      },
      {
        "@type": "HowToStep",
        "position": 4,
        "name": "Verification & Reward",
        "text": "The FLUMBERICO team verifies link quality using tools like Ahrefs and Moz. Rewards are calculated based on domain authority, link placement, and overall SEO value.",
        "itemListElement": [
          {
            "@type": "HowToDirection",
            "text": "Links are verified for authenticity and quality"
          },
          {
            "@type": "HowToDirection",
            "text": "Rewards are weighted by domain authority (DA 70+ earns maximum tokens)"
          },
          {
            "@type": "HowToDirection",
            "text": "Tokens are distributed to your provided Solana wallet address"
          }
        ]
      }
    ],
    "supply": [
      {
        "@type": "HowToSupply",
        "name": "Solana Wallet"
      },
      {
        "@type": "HowToSupply",
        "name": "SEO Knowledge"
      },
      {
        "@type": "HowToSupply",
        "name": "Content Creation Skills"
      }
    ],
    "tool": [
      {
        "@type": "HowToTool",
        "name": "Ahrefs or Moz for checking Domain Authority"
      },
      {
        "@type": "HowToTool",
        "name": "Telegram for submission"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-black text-white pt-32 pb-20">
      <Helmet>
        <link rel="canonical" href="https://www.flumberico.fun/how-to-earn" />
        <script type="application/ld+json">
          {JSON.stringify(howToSchema)}
        </script>
      </Helmet>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <Award className="w-20 h-20 text-gold mx-auto mb-6" />
          <h1 className="text-6xl font-black mb-6 text-gold">
            How to Earn $FLUMBERICO
          </h1>
          <p className="text-2xl text-silver font-semibold">Ranking is Banking!</p>
        </div>

        {/* Overview */}
        <section className="mb-16 bg-gradient-to-br from-gold/10 to-transparent border border-gold/30 rounded-lg p-8">
          <h2 className="text-3xl font-bold text-gold mb-6">The Concept</h2>
          <p className="text-white text-lg leading-relaxed mb-4">
            Earn $FLUMBERICO tokens by helping to rank the keyword "flumberico" on Google. The more authoritative your backlinks, the more tokens you receive. It's simple: <span className="text-gold font-bold">better links = more earnings</span>.
          </p>
          <p className="text-white text-lg leading-relaxed">
            During the <span className="text-gold font-semibold">launch phase</span>, tokens are manually distributed by the developer based on verified link quality. In the future, this process will be fully automated via smart contracts.
          </p>
        </section>

        {/* Step-by-Step Guide */}
        <section className="mb-16">
          <h2 className="text-4xl font-bold text-center mb-12 text-gold">
            Step-by-Step Earning Guide
          </h2>
          
          <div className="space-y-8">
            {/* Step 1 */}
            <div className="bg-gradient-to-r from-gold/20 to-black/50 border border-gold/40 rounded-lg p-8">
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gold rounded-full flex items-center justify-center">
                    <span className="text-3xl font-black text-black">1</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gold mb-3">Create Quality Content</h3>
                  <p className="text-white mb-4 leading-relaxed">
                    Write high-quality, relevant content about FLUMBERICO, SEO competitions, meme coins, or related topics. The content should naturally include links to <span className="font-semibold text-gold">flumberico.fun</span>.
                  </p>
                  <div className="bg-black/50 rounded-lg p-4">
                    <p className="text-sm text-white mb-2 font-semibold">Content Ideas:</p>
                    <ul className="list-disc list-inside text-silver/70 text-sm space-y-1">
                      <li>Blog posts about the FLUMBERICO experiment</li>
                      <li>SEO case studies featuring flumberico</li>
                      <li>Crypto news articles mentioning $FLUMBERICO</li>
                      <li>Tutorial videos about link building</li>
                      <li>Social media threads and viral content</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="bg-gradient-to-r from-silver/20 to-black/50 border border-silver/40 rounded-lg p-8">
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-silver rounded-full flex items-center justify-center">
                    <span className="text-3xl font-black text-black">2</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-silver mb-3">Build High-Authority Backlinks</h3>
                  <p className="text-white mb-4 leading-relaxed">
                    Place your content on high-authority domains. The higher the Domain Authority (DA), Domain Rating (DR), or similar metrics, the more $FLUMBERICO you'll earn.
                  </p>
                  <div className="bg-black/50 rounded-lg p-4">
                    <p className="text-sm text-white mb-2 font-semibold">High-Value Link Sources:</p>
                    <ul className="list-disc list-inside text-silver/70 text-sm space-y-1">
                      <li>DA 50+ websites (premium rewards)</li>
                      <li>Industry-relevant publications</li>
                      <li>Popular blogs and news sites</li>
                      <li>Educational (.edu) and government (.gov) domains</li>
                      <li>High-traffic social platforms</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-gradient-to-r from-gold/20 to-black/50 border border-gold/40 rounded-lg p-8">
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gold rounded-full flex items-center justify-center">
                    <span className="text-3xl font-black text-black">3</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gold mb-3">Submit Your Links</h3>
                  <p className="text-white mb-4 leading-relaxed">
                    Once your content is published and the backlink is live, submit it for verification. Include the source URL, target page, and anchor text used.
                  </p>
                  <div className="bg-black/50 rounded-lg p-4">
                    <p className="text-sm text-white mb-2 font-semibold">Required Information:</p>
                    <ul className="list-disc list-inside text-silver/70 text-sm space-y-1">
                      <li>Source URL (where the link is placed)</li>
                      <li>Domain Authority / Domain Rating</li>
                      <li>Target URL (flumberico.fun page)</li>
                      <li>Anchor text and link type (follow/nofollow)</li>
                      <li>Your wallet address for token distribution</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="bg-gradient-to-r from-silver/20 to-black/50 border border-silver/40 rounded-lg p-8">
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-silver rounded-full flex items-center justify-center">
                    <span className="text-3xl font-black text-black">4</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-silver mb-3">Verification & Distribution</h3>
                  <p className="text-white mb-4 leading-relaxed">
                    The developer verifies your link quality, domain metrics, and link equity. Once approved, $FLUMBERICO tokens are distributed to your wallet based on the calculated reward value.
                  </p>
                  <div className="bg-black/50 rounded-lg p-4">
                    <p className="text-sm text-white mb-2 font-semibold">Verification Process:</p>
                    <ul className="list-disc list-inside text-silver/70 text-sm space-y-1">
                      <li>Link validation (live and functional)</li>
                      <li>Domain authority verification</li>
                      <li>Content quality assessment</li>
                      <li>Link juice and equity evaluation</li>
                      <li>Automated distribution (coming soon!)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Reward Calculator */}
        <section className="mb-16">
          <h2 className="text-4xl font-bold text-center mb-12 text-gold">
            Reward Factors
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-black/50 border border-gold/30 rounded-lg p-6">
              <TrendingUp className="w-12 h-12 text-gold mb-4" />
              <h3 className="text-xl font-bold text-gold mb-3">Domain Authority</h3>
              <p className="text-white mb-4">Higher authority = Higher rewards</p>
              <ul className="space-y-2 text-sm text-silver/70">
                <li className="flex items-center"><CheckCircle className="w-4 h-4 text-gold mr-2" /> DA 70+: Premium tier (5x multiplier)</li>
                <li className="flex items-center"><CheckCircle className="w-4 h-4 text-gold mr-2" /> DA 50-69: High tier (3x multiplier)</li>
                <li className="flex items-center"><CheckCircle className="w-4 h-4 text-gold mr-2" /> DA 30-49: Medium tier (2x multiplier)</li>
                <li className="flex items-center"><CheckCircle className="w-4 h-4 text-silver mr-2" /> DA 10-29: Standard tier (1x multiplier)</li>
              </ul>
            </div>

            <div className="bg-black/50 border border-silver/30 rounded-lg p-6">
              <Link2 className="w-12 h-12 text-silver mb-4" />
              <h3 className="text-xl font-bold text-silver mb-3">Link Quality</h3>
              <p className="text-white mb-4">Better links = Better earnings</p>
              <ul className="space-y-2 text-sm text-silver/70">
                <li className="flex items-center"><CheckCircle className="w-4 h-4 text-silver mr-2" /> DoFollow links: Full reward</li>
                <li className="flex items-center"><CheckCircle className="w-4 h-4 text-silver mr-2" /> NoFollow links: 50% reward</li>
                <li className="flex items-center"><CheckCircle className="w-4 h-4 text-silver mr-2" /> Contextual links: +20% bonus</li>
                <li className="flex items-center"><CheckCircle className="w-4 h-4 text-silver mr-2" /> Viral content: +50% bonus</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Best Practices */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gold mb-8">Best Practices for Maximum Earnings</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-green-900/20 to-black/50 border border-green-500/40 rounded-lg p-6">
              <h3 className="text-xl font-bold text-green-400 mb-4 flex items-center">
                <CheckCircle className="mr-2" /> DO These Things
              </h3>
              <ul className="space-y-2 text-white text-sm">
                <li>✓ Focus on high-DA domains</li>
                <li>✓ Create genuinely valuable content</li>
                <li>✓ Use natural, contextual anchor text</li>
                <li>✓ Build diverse link profiles</li>
                <li>✓ Engage in social promotion</li>
                <li>✓ Follow ethical SEO practices</li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-red-900/20 to-black/50 border border-red-500/40 rounded-lg p-6">
              <h3 className="text-xl font-bold text-red-400 mb-4 flex items-center">
                <Zap className="mr-2" /> AVOID These Things
              </h3>
              <ul className="space-y-2 text-white text-sm">
                <li>✗ Spammy or low-quality links</li>
                <li>✗ Link farms and PBNs</li>
                <li>✗ Duplicate or thin content</li>
                <li>✗ Unnatural anchor text patterns</li>
                <li>✗ Black-hat SEO tactics</li>
                <li>✗ Fraudulent link submissions</li>
              </ul>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center bg-gradient-to-br from-gold/20 via-silver/10 to-gold/20 border border-gold/40 rounded-lg p-12">
          <h2 className="text-4xl font-black mb-6 text-gold">
            Ready to Start Earning?
          </h2>
          <p className="text-xl text-white mb-4">
            Submit your backlinks and claim $FLUMBERICO tokens through our official Telegram channel
          </p>
          <p className="text-lg text-silver mb-8">
            Join @flumberico - the <span className="text-gold font-bold">only official</span> channel for token claims
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://t.me/flumberico"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="font-bold text-lg px-8 py-6 rounded-full flex items-center gap-2" style={{ backgroundColor: '#FFD700', color: '#000000' }}>
                <span>📱</span> Join Telegram & Claim Tokens
              </Button>
            </a>
            <a
              href="https://pump.fun/coin/Hu8PvhDSyVwNUYnXsWQ12DEX2baydRximcbaSAkKpump"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="font-bold text-lg px-8 py-6 rounded-full" style={{ backgroundColor: '#FFFFFF', color: '#000000' }}>
                Buy $FLUMBERICO
              </Button>
            </a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default HowToEarn;